import { create } from 'zustand';

/**
 * Device-preview state — hoisted out of DevicePreview.tsx so the
 * Tablet Mode toggle can live in the AppShell header (next to the
 * Demo Mode button) while the actual viewport-frame chrome still
 * renders from DevicePreview.
 *
 * Persists to localStorage so the choice survives reloads.
 */

export type PresetKey =
  | 'off'
  | 'ipad-10'
  | 'ipad-pro-11'
  | 'ipad-pro-13'
  | 'ipad-mini'
  | 'iphone-15'
  | 'iphone-15-pro-max';

export type Orientation = 'portrait' | 'landscape';

interface State {
  preset: PresetKey;
  orientation: Orientation;
  setPreset: (p: PresetKey) => void;
  setOrientation: (o: Orientation) => void;
  /** Quick toggle — flips between OFF and the iPad 10 (the most
   *  common pilot tablet) so the chiefs can preview the tablet
   *  layout from desktop with one tap. */
  toggleTablet: () => void;
}

const STORAGE_KEY = 'emspcr-device-preview';

interface Stored {
  preset: PresetKey;
  orientation: Orientation;
}

function readStored(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Stored;
  } catch { /* ignore */ }
  return { preset: 'off', orientation: 'portrait' };
}

function writeStored(s: Stored) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

const initial = readStored();

export const useDevicePreviewStore = create<State>((set, get) => ({
  preset: initial.preset,
  orientation: initial.orientation,
  setPreset(p) {
    set({ preset: p });
    writeStored({ preset: p, orientation: get().orientation });
  },
  setOrientation(o) {
    set({ orientation: o });
    writeStored({ preset: get().preset, orientation: o });
  },
  toggleTablet() {
    const cur = get().preset;
    const next: PresetKey = cur === 'off' ? 'ipad-10' : 'off';
    set({ preset: next });
    writeStored({ preset: next, orientation: get().orientation });
  },
}));
